import React from "react";
import PropTypes from "prop-types";
import { EditorState, convertToRaw, convertFromRaw, Modifier } from "draft-js";
import PluginsEditor from "draft-js-plugins-editor";
import createCounterPlugin from "draft-js-counter-plugin";
import classNames from "classnames/bind";
import throttle from "lodash/throttle";

import stateFromHTML from "./stateFromHTML";
import createMentionsPlugin from "./plugins/mentionsPlugin";
import createSpoilersPlugin from "./plugins/spoilersPlugin";
import styles from "./styles/Editor.sass";

const cx = classNames.bind(styles);

class Editor extends React.Component {
  constructor(props) {
    super(props);

    let contentState;
    if (props.bodySerialized) {
      contentState = convertFromRaw(JSON.parse(props.bodySerialized));
    } else if (props.body) {
      contentState = stateFromHTML(props.body.replace(/<spoiler>/gi, "<audio>").replace(/<\/spoiler>/gi, "</audio>"), {
        customInlineFn: (element, { Entity }) => {
          if (element.tagName === "A") {
            return Entity("mention", { mention: { name: element.getAttribute("title"), link: element.getAttribute("href") } }, "SEGMENTED");
          }
          return null;
        },
        elementStyles: { audio: "SPOILER" },
      });
    }

    let editorState;
    if (contentState == null) {
      editorState = EditorState.createEmpty();
    } else {
      editorState = EditorState.createWithContent(contentState);
    }

    this.state = {
      editorState,
      suggestions: [],
    };

    const getWrapper = () => this.wrapper;

    this.mentionsPlugin = createMentionsPlugin({
      getWrapper,
      setSuggestions: (suggestions = []) => { this.setState({ suggestions }); },
    });

    this.counterPlugin = createCounterPlugin({
      theme: {
        counter: cx("counterError"),
        counterOverLimit: cx("counterWarn"),
      },
    });

    this.spoilersPlugin = createSpoilersPlugin({
      getWrapper,
      readOnly: props.readOnly,
    });
    this.toggleSpoiler = this.spoilersPlugin.toggleSpoiler;

    this.performCallbacks = throttle(this.performCallbacks.bind(this), 100);
  }

  componentDidMount() {
    const { readOnly } = this.props;
    if (readOnly) { setTimeout(this.spoilersPlugin.addSpoilerEventListeners); }
  }

  componentDidUpdate(prevProps) {
    const { bodySerialized } = this.props;
    if (bodySerialized === "" && prevProps.bodySerialized !== "") {
      this.onChange(EditorState.createEmpty());
    }
  }

  componentWillUnmount() {
    const { readOnly } = this.props;
    if (readOnly) { this.spoilersPlugin.removeSpoilerEventListeners(); }
  }

  onChange = (editorState) => {
    this.setState({ editorState }, this.performCallbacks);
  }

  performCallbacks = () => {
    const { onBodyChange, onInlineStyleChange } = this.props;
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    onBodyChange(JSON.stringify(convertToRaw(contentState)), contentState.getPlainText("").length);
    onInlineStyleChange(editorState.getCurrentInlineStyle());
  }

  render() {
    const { onFocusChange, placeholder, readOnly } = this.props;
    const { editorState, suggestions } = this.state;
    const { MentionSuggestions, SuggestionEntry, onSearchChange, ...mentionsPlugin } = this.mentionsPlugin;
    const { CustomCounter } = this.counterPlugin;
    const plugins = [mentionsPlugin, this.counterPlugin, this.spoilersPlugin];

    return (
      <div
        className={cx("wrapper", { editable: !readOnly })}
        key="wrapper"
        ref={(el) => { if (this.wrapper == null && el != null) { this.wrapper = el; } }}
      >
        <PluginsEditor
          editorState={editorState}
          onChange={this.onChange}
          onBlur={() => { onFocusChange(false); }}
          onFocus={() => { onFocusChange(); }}
          plugins={plugins}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={(el) => { if (this.editor == null && el != null) this.editor = el; }}
          handlePastedText={(text) => {
            const newState = Modifier.replaceText(editorState.getCurrentContent(), editorState.getSelection(), text.trim());
            this.onChange(EditorState.push(editorState, newState, "insert-fragment"));
            return "handled";
          }}
        />
        <MentionSuggestions
          onSearchChange={onSearchChange}
          suggestions={suggestions}
          entryComponent={SuggestionEntry}
        />
        {!readOnly
          && (
            <div className={cx("counterWrapper")}>
              <CustomCounter
                className={cx("counter")}
                editorState={editorState}
                limit={-1}
                countFunction={str => (str.length > 449 ? 500 - str.length : null)}
              />
            </div>
          )
        }
      </div>);
  }
}

Editor.propTypes = {
  body: PropTypes.string,
  bodySerialized: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  onBodyChange: PropTypes.func,
  onInlineStyleChange: PropTypes.func,
  onFocusChange: PropTypes.func,
};

const noOp = () => {};
Editor.defaultProps = {
  body: "",
  bodySerialized: "",
  placeholder: null,
  readOnly: false,
  onBodyChange: noOp,
  onInlineStyleChange: noOp,
  onFocusChange: noOp,
};

export default Editor;
